import { z } from 'zod';
import { type Actor, actorSchema } from './actor';
import { assertNever } from './exhaustive';
import { isNullaryOperator } from './field-types';
import { FILTER_OPERATOR_LABELS, RELATIVE_DATE_LABELS } from './filter-labels';
import { newId } from './ids';
import { isRelativeDateOperand, type ViewFilterNode, viewFilterNodeSchema } from './view';

/**
 * THE WORKFLOW VOCABULARY (docs/decisions/workflow-builder.md).
 *
 * A workflow is `WHEN -> ONLY IF -> THEN`, read as a sentence and edited as a LIST (D36).
 * This file is the whole contract: what can start a run, what a run may do, how a value
 * slot is filled, and the two strings the causal guard depends on.
 *
 * It lives in `shared` because three consumers must agree on every shape (INV-11): the
 * server validates and executes it, the browser edits it, and both render the same
 * sentence. There is no build step, so there is exactly one definition of each.
 *
 * WHAT IS DELIBERATELY NOT HERE: a template language. A value slot is a LITERAL, a
 * FIELD reference, a clock or an explicit CLEAR (D18) - never `{{ }}`. The argument is
 * in D18 and the consequence is visible in `ValueSource`: five closed variants, each
 * type-checkable against the field it is being written into.
 */

// --- the causal edge (D25) ---------------------------------------------------

/**
 * WHAT A WORKFLOW WRITE CARRIES ON ITS ACTOR, and why it is a string.
 *
 * `Actor.context` is `string | undefined`, max 500 (`actor.ts:25,33`), and its docblock
 * already anticipates "workflow id". Widening the actor with typed fields would change
 * INV-3's stated shape (`ARCHITECTURE.md:70-74`), which is a locked-invariant change, so
 * the causal edge is ENCODED into the string that already travels with every write.
 *
 * That it travels is the whole point (D29): the actor lands on `meta.record_event.actor`,
 * on `meta.pending_change.proposed_by` and on `meta.record_task.created_by`, so the depth
 * of the run that wrote a value is readable from the event whatever record it landed on -
 * including an approval applied three days later. The alternative, inferring depth from a
 * run lookup keyed on the SUBJECT record, is inert for a relation-target write, for a
 * create, and for every approval-applied write; that is red-team finding C1.
 */
export interface WorkflowActorRef {
  readonly workflowId: string;
  /** The depth of the RUN THAT WROTE THIS, 0-based. A consumer adds one. */
  readonly depth: number;
  /** The run at depth 0 that started the chain. Its own id at depth 0. */
  readonly rootRunId: string;
}

/** `workflow:<uuid>:d<n>:r<uuid>` - 86 characters at depth 0-9, against a 500 cap. */
export const WORKFLOW_ACTOR_CONTEXT_PREFIX = 'workflow:';

/**
 * `<n>` is plain decimal with no fixed width, because `WORKFLOW_MAX_DEPTH` is a config
 * value an operator may set above 9 and each extra digit costs one character.
 */
export function formatWorkflowActorContext(ref: WorkflowActorRef): string {
  return `${WORKFLOW_ACTOR_CONTEXT_PREFIX}${ref.workflowId}:d${ref.depth}:r${ref.rootRunId}`;
}

const UUID_SOURCE = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';

/**
 * STRICT, and that is a safety property rather than tidiness.
 *
 * Three other writers put arbitrary text in the same column: the importer's file name
 * (`seed/apply.ts`), an API key's label (`auth/service.ts`) and the identity detector's
 * marker (`identity/context.ts`). A fuzzy parse inside a loop guard would read one of
 * those as a workflow write. Anything that is not exactly this shape is "not a workflow
 * write", which starts a fresh chain at depth 0 - the FAIL-OPEN direction, chosen
 * deliberately in risk 3: refusing to run on an unparseable string would make a
 * formatting bug look like a broken product.
 */
const ACTOR_CONTEXT_PATTERN = new RegExp(
  `^workflow:(${UUID_SOURCE}):d(\\d{1,3}):r(${UUID_SOURCE})$`,
  'i',
);

export function parseWorkflowActorContext(
  context: string | undefined,
): WorkflowActorRef | undefined {
  if (context === undefined) return undefined;
  const match = context.match(ACTOR_CONTEXT_PATTERN);
  if (match === null) return undefined;
  const [, workflowId, depth, rootRunId] = match;
  if (workflowId === undefined || depth === undefined || rootRunId === undefined) return undefined;
  return { workflowId, depth: Number(depth), rootRunId };
}

/**
 * The SQL prefix D21's dedupe matches on, and the ONLY other place a workflow identity is
 * compared. It is a prefix and not an equality because depth and root differ between runs
 * of the same workflow (obligation 17).
 */
export function workflowActorContextPrefix(workflowId: string): string {
  return `${WORKFLOW_ACTOR_CONTEXT_PREFIX}${workflowId}:`;
}

/**
 * The actor every workflow write carries (INV-3). `userId` is never set: a workflow is
 * not a person, so it can never be "the author" (`annotations/context.ts:38-44`).
 */
export function workflowActor(ref: WorkflowActorRef): Actor {
  return { source: 'WORKFLOW', context: formatWorkflowActorContext(ref) };
}

// --- triggers (D8) -----------------------------------------------------------

/**
 * The DATA-CHANGE subset of the eight core actions. The operator signals
 * (`write_blocked`, `lock_released`, `proposal_expired`, `erased`), the four merge actions
 * and the eleven annotation actions are excluded from the v1 picker for the reason
 * `webhooks.md:90-97` gives: the line is data changes versus operator signals, and a coach
 * building "when a customer goes red" must not scroll past `merge_repointed` to find
 * `updated`. `restored` is excluded as the inverse of a state the picker does not offer.
 */
export const WORKFLOW_TRIGGER_ACTIONS = ['created', 'updated', 'soft_deleted'] as const;
export type WorkflowTriggerAction = (typeof WORKFLOW_TRIGGER_ACTIONS)[number];

export const WORKFLOW_TRIGGER_KINDS = ['record_event', 'schedule', 'manual'] as const;
export type WorkflowTriggerKind = (typeof WORKFLOW_TRIGGER_KINDS)[number];

/**
 * THE CONDITION IS NOT IN HERE. It is its own `condition jsonb` column on
 * `meta.workflow_version`, so the merge's WHOLE-COLUMN rewriter
 * (`db/identity-store/stored-filters.ts`) works unchanged (D4, red-team M1). The API and
 * the UI still present the two as one document; only the storage splits.
 */
export type WorkflowTrigger =
  | {
      readonly kind: 'record_event';
      readonly objectMetadataId: string;
      readonly actions: readonly WorkflowTriggerAction[];
      /** The field gate (D10). Empty or absent means "any field". */
      readonly fieldIds?: readonly string[] | undefined;
    }
  | { readonly kind: 'schedule'; readonly cron: string; readonly objectMetadataId: string }
  | { readonly kind: 'manual'; readonly objectMetadataId: string };

/** Every trigger names exactly one object: D2's one-subject-record rule, at the top. */
export function triggerObjectId(trigger: WorkflowTrigger): string {
  return trigger.objectMetadataId;
}

/**
 * A FIVE-FIELD CRON, validated here rather than discovered by pg-boss's timekeeper at
 * 08:00 on a Monday.
 *
 * The grammar is the intersection every cron implementation agrees on - `*`, a number, a
 * range, a step and a comma list - checked against each field's own range. Names
 * (`MON`, `JAN`) are deliberately NOT accepted: the editor emits numbers, and accepting a
 * second spelling would mean two things to render back.
 */
const CRON_RANGES: readonly (readonly [number, number])[] = [
  [0, 59],
  [0, 23],
  [1, 31],
  [1, 12],
  [0, 6],
];

function isCronField(part: string, min: number, max: number): boolean {
  return part.split(',').every((entry) => {
    const [range, step, extra] = entry.split('/');
    if (extra !== undefined) return false;
    if (range === undefined || range === '') return false;
    if (step !== undefined && (!/^\d+$/.test(step) || Number(step) < 1)) return false;
    if (range === '*') return true;
    const bounds = range.split('-');
    if (bounds.length > 2) return false;
    return bounds.every((bound) => {
      if (!/^\d+$/.test(bound)) return false;
      const value = Number(bound);
      return value >= min && value <= max;
    });
  });
}

export function isValidCron(expression: string): boolean {
  const parts = expression.trim().split(/\s+/);
  if (parts.length !== 5) return false;
  return parts.every((part, index) => {
    const range = CRON_RANGES[index];
    return range !== undefined && isCronField(part, range[0], range[1]);
  });
}

// --- targets and values (D18, D19) -------------------------------------------

/**
 * WHAT A STEP ACTS ON. One relation hop, never two - the same budget `dashboard.md` set
 * for its panels. It buys the case that comes up ("when an enrollment goes red, flag its
 * agency") and stops short of a join language.
 */
export type StepTarget =
  | { readonly kind: 'trigger_record' }
  /** A MANY_TO_ONE field ON the trigger record. An empty relation SKIPS the step (D19). */
  | { readonly kind: 'relation'; readonly fieldId: string }
  /** The record an earlier `create_record` step made. */
  | { readonly kind: 'step_record'; readonly stepId: string };

export const STEP_TARGET_KINDS = ['trigger_record', 'relation', 'step_record'] as const;

/**
 * A VALUE SLOT, and the reason there is no sixth variant.
 *
 * `empty` is required rather than rounding-out (red-team M5): D26's worked example
 * ("clear the flag when it clears") cannot be built without it, and `provenance.md` Q2
 * makes clearing a first-class correction. It writes the field's OWN null representation
 * through the field-type registry, which is the same path a human clearing a field takes;
 * `{kind:'literal', value:null}` was the alternative and is rejected because "null" means
 * twelve different things across twelve field types.
 */
export type ValueSource =
  | { readonly kind: 'literal'; readonly value: unknown }
  /**
   * `cast: 'date'` reads a DATETIME and writes the DAY it falls on (`phase-b-coach.md` D21).
   *
   * THE ONE CAST, and it exists because two temporal families meet on one line of the
   * product: a call happened at an instant (`call.occurred_at`, DATETIME) and the checkpoint
   * it satisfies is a day (`cohort_enrollment.onboarding_call_at`, DATE). Without it that
   * playbook is unbuildable, because `validateSource` refuses a copy between two types and
   * is right to - a coercion TABLE would make every field pair a rule somebody has to learn.
   *
   * It is a NARROWING that can never fail: every instant falls on exactly one day. The
   * reverse (a DATE into a DATETIME) is deliberately not offered, because it would have to
   * invent a time and a zone.
   */
  | {
      readonly kind: 'field';
      readonly target: StepTarget;
      readonly fieldId: string;
      readonly cast?: 'date' | undefined;
    }
  /** DATE / DATETIME only. */
  | { readonly kind: 'now' }
  /** DATE only. Negative days are legal: "two days ago" is as real as "in two days". */
  | { readonly kind: 'today_plus_days'; readonly days: number }
  | { readonly kind: 'empty' };

export const VALUE_SOURCE_KINDS = ['literal', 'field', 'now', 'today_plus_days', 'empty'] as const;
export type ValueSourceKind = (typeof VALUE_SOURCE_KINDS)[number];

export interface WorkflowValueAssignment {
  readonly fieldId: string;
  readonly source: ValueSource;
}

// --- steps (D17) -------------------------------------------------------------

export const WORKFLOW_STEP_KINDS = [
  'update_record',
  'create_record',
  'create_task',
  'http_request',
  'propose_change',
] as const;
export type WorkflowStepKind = (typeof WORKFLOW_STEP_KINDS)[number];

/**
 * D28's replay table, as data. A worker killed mid-step leaves a step row `running` with
 * an unknown outcome; re-executing is safe for some kinds and not for others, so each kind
 * DECLARES it rather than the executor guessing.
 *
 * `create_task` is replay-safe BECAUSE of D21's windowed dedupe, not despite it - a retry
 * always falls inside its own window. `create_record` is not, and cannot be made so:
 * `createRecord` mints its own id (`records/write.ts:245`), so there is no conflict to
 * catch.
 */
export const REPLAY_SAFE_STEP_KINDS: ReadonlySet<WorkflowStepKind> = new Set<WorkflowStepKind>([
  'update_record',
  'create_task',
]);

export function isReplaySafeStep(kind: WorkflowStepKind): boolean {
  return REPLAY_SAFE_STEP_KINDS.has(kind);
}

export interface WorkflowHttpHeader {
  readonly name: string;
  readonly value: string;
}

interface StepBase {
  /** Stable across edits and reorders (D36): it is what a run's step rows point at. */
  readonly id: string;
}

export interface UpdateRecordStep extends StepBase {
  readonly kind: 'update_record';
  readonly target: StepTarget;
  readonly values: readonly WorkflowValueAssignment[];
}

export interface CreateRecordStep extends StepBase {
  readonly kind: 'create_record';
  readonly objectMetadataId: string;
  readonly values: readonly WorkflowValueAssignment[];
}

/**
 * WHO A PLAYBOOK'S TASK BELONGS TO (`phase-b-coach.md` D9).
 *
 * `user` is the existing behaviour with a name: a fixed person, chosen in the editor.
 * `relation_user` is the one a playbook needs - "the customer's CSM" - and it is a HOP plus
 * a field rather than a user id, because the answer is different for every record the
 * workflow runs on.
 *
 * HOW A RECORD BECOMES A USER. `fieldId` is a RELATION on the object `target` names, and the
 * object it points at (`team_member`) carries an EMAIL field declared as an identifier in
 * the `email` namespace; that e-mail is matched against `meta."user".email`. The convention
 * is the data model's own (`seed/model.ts`), and it is what lets a playbook assign work
 * without the CRM holding a second copy of who is who.
 *
 * NOBODY IS NOT A FAILURE. A record with no CSM, or a CSM with no account, yields an
 * UNASSIGNED task - never a broken run. The alternative would switch a customer's whole
 * onboarding off because one row is incomplete.
 */
export type TaskAssignee =
  | { readonly kind: 'user'; readonly userId: string }
  | { readonly kind: 'relation_user'; readonly target: StepTarget; readonly fieldId: string };

export interface CreateTaskStep extends StepBase {
  readonly kind: 'create_task';
  readonly target: StepTarget;
  /** A literal (D18). A task created ON the record already reads with the record's name. */
  readonly title: string;
  /**
   * D9's predecessor, kept because live workflows carry it and deprecated in the editor.
   * Setting both this and `assignee` is refused at save time: two answers to one question.
   */
  readonly assigneeUserId?: string | null | undefined;
  readonly assignee?: TaskAssignee | null | undefined;
  /**
   * `meta.record_task.due_date` is DATE, no time, on purpose
   * (`0008_annotations.ts:108`), so the UI must not offer one and the executor renders
   * whatever source is here as a day.
   */
  readonly dueDate?: ValueSource | null | undefined;
}

export interface HttpRequestStep extends StepBase {
  readonly kind: 'http_request';
  /** Whose record goes in the body. */
  readonly target: StepTarget;
  readonly url: string;
  readonly headers: readonly WorkflowHttpHeader[];
  /** Off by default: an n8n flow behind the same Caddy does not need HMAC (D22). */
  readonly sign: boolean;
}

export interface ProposeChangeStep extends StepBase {
  readonly kind: 'propose_change';
  readonly target: StepTarget;
  readonly values: readonly WorkflowValueAssignment[];
}

export type WorkflowStep =
  | UpdateRecordStep
  | CreateRecordStep
  | CreateTaskStep
  | HttpRequestStep
  | ProposeChangeStep;

/**
 * TEN, and it is a design limit rather than a technical one: a list of eleven things is a
 * program, and a program belongs in the n8n on the same VPS.
 */
export const WORKFLOW_MAX_STEPS = 10;
export const WORKFLOW_NAME_MAX = 120;
export const WORKFLOW_TASK_TITLE_MAX = 200;
export const WORKFLOW_MAX_HTTP_HEADERS = 10;

/** `st_` plus twelve hex, the `newSelectOptionId` shape: short, opaque, stable. */
export const WORKFLOW_STEP_ID_PATTERN = /^st_[0-9a-f]{12}$/;

/**
 * Twelve hex from the RANDOM tail of a uuidv7, never the leading part - our ids carry a
 * millisecond timestamp in front, so a prefix is identical for everything minted in the
 * same window (the same trap `newSelectOptionId` documents).
 */
export function newWorkflowStepId(): string {
  return `st_${newId().replaceAll('-', '').slice(-12)}`;
}

// --- versions, runs and the wire shapes --------------------------------------

export const WORKFLOW_VERSION_STATUSES = ['draft', 'active', 'archived'] as const;
export type WorkflowVersionStatus = (typeof WORKFLOW_VERSION_STATUSES)[number];

/**
 * `waiting` is RESERVED and unreachable in v1 (D4, red-team M8): nothing produces one and
 * an executor that meets one logs at `warn` and leaves the run alone. It is in the CHECK
 * from day one because the repo's precedent for widening a status CHECK is drop-plus-add
 * (`0006_identity.ts:287-291`), which `migrations.md` does not classify as cheap on a
 * populated table - and the table has no rows today. PC-3 is what it is reserved for.
 */
export const WORKFLOW_RUN_STATUSES = [
  'queued',
  'running',
  'succeeded',
  'failed',
  'interrupted',
  'cancelled',
  'blocked_recursion',
  'waiting',
] as const;
export type WorkflowRunStatus = (typeof WORKFLOW_RUN_STATUSES)[number];

export const WORKFLOW_RUN_STEP_STATUSES = [
  'running',
  'succeeded',
  'failed',
  'blocked',
  'skipped',
  'unknown',
] as const;
export type WorkflowRunStepStatus = (typeof WORKFLOW_RUN_STEP_STATUSES)[number];

/**
 * D34's three segments. `Failed` is what went wrong; `Stopped` is what a GUARD stopped,
 * which is a different sentence and must not be filed under failure - a recursion ceiling
 * doing its job is the system working (red-team N10).
 */
export const WORKFLOW_RUN_SEGMENTS = ['all', 'failed', 'stopped'] as const;
export type WorkflowRunSegment = (typeof WORKFLOW_RUN_SEGMENTS)[number];

export const WORKFLOW_FAILED_STATUSES: readonly WorkflowRunStatus[] = ['failed', 'interrupted'];
export const WORKFLOW_STOPPED_STATUSES: readonly WorkflowRunStatus[] = [
  'blocked_recursion',
  'cancelled',
];
/**
 * The badge counts ATTENTION rows, not failures (D34). Moving `blocked_recursion` into
 * `Stopped` was right for the copy and would have been wrong for the signal: a workflow
 * repeatedly hitting the ceiling would be visible only inside a segment nobody opens.
 * `cancelled` is excluded - somebody pulled the brake on purpose and does not need telling.
 */
export const WORKFLOW_ATTENTION_STATUSES: readonly WorkflowRunStatus[] = [
  'failed',
  'interrupted',
  'blocked_recursion',
];

export function statusesForSegment(segment: WorkflowRunSegment): readonly WorkflowRunStatus[] {
  switch (segment) {
    case 'all':
      return WORKFLOW_RUN_STATUSES;
    case 'failed':
      return WORKFLOW_FAILED_STATUSES;
    case 'stopped':
      return WORKFLOW_STOPPED_STATUSES;
    default:
      return assertNever(segment, 'statusesForSegment');
  }
}

/** Reruns are offered on these two only: a chain that got too long is not one to re-fire. */
export function isRerunnableStatus(status: WorkflowRunStatus): boolean {
  return status === 'failed' || status === 'interrupted';
}

export interface WorkflowVersionDocument {
  readonly trigger: WorkflowTrigger;
  readonly condition: ViewFilterNode | null;
  readonly steps: readonly WorkflowStep[];
}

export interface WorkflowVersion extends WorkflowVersionDocument {
  readonly id: string;
  readonly workflowId: string;
  readonly version: number;
  readonly status: WorkflowVersionStatus;
  readonly createdAt: string;
  readonly activatedAt: string | null;
  readonly archivedAt: string | null;
}

export interface WorkflowSummary {
  readonly id: string;
  readonly name: string;
  readonly description: string | null;
  readonly isEnabled: boolean;
  readonly pausedReason: string | null;
  readonly consecutiveFailures: number;
  readonly createdAt: string;
  readonly updatedAt: string;
  /** The ACTIVE version's trigger, or the draft's when nothing is active yet. */
  readonly trigger: WorkflowTrigger;
  /** D5's loud draft: true when the draft differs from what is live. */
  readonly hasUnactivatedChanges: boolean;
  readonly isActive: boolean;
  /** D34/D44: runs needing attention in the last 24 hours. */
  readonly attentionCount: number;
}

export interface WorkflowDetail extends WorkflowSummary {
  readonly draft: WorkflowVersion;
  readonly active: WorkflowVersion | null;
}

export interface WorkflowRunStepView {
  readonly stepIndex: number;
  readonly stepId: string;
  readonly kind: string;
  readonly status: WorkflowRunStepStatus;
  /**
   * SCRUBBED AGAINST THE VIEWER, NOT AGAINST THE WORKFLOW'S ROLE
   * (roles-and-permissions.md D14.1 part 1).
   *
   * A run executed with wide authority is legitimate; showing its captured values to
   * somebody who could not have read those records is not. When a step's result names an
   * object the viewer may not read, it degrades to null and the step keeps its KIND and
   * STATUS - the outcome, which any `WORKFLOWS_RUN` holder is entitled to (part 2).
   */
  readonly result: Readonly<Record<string, unknown>> | null;
  readonly error: string | null;
  readonly startedAt: string;
  readonly finishedAt: string | null;
}

export interface WorkflowRunView {
  readonly id: string;
  readonly workflowId: string;
  readonly workflowVersionId: string;
  readonly version: number;
  readonly triggerKind: WorkflowTriggerKind;
  readonly scheduleFireId: string | null;
  readonly objectMetadataId: string;
  readonly recordId: string;
  /** Resolved through the record layer; null when the record is gone (D41). */
  readonly recordLabel: string | null;
  readonly status: WorkflowRunStatus;
  readonly depth: number;
  readonly rootRunId: string | null;
  readonly blockedFields: number;
  readonly error: string | null;
  readonly startedBy: Actor | null;
  readonly startedByName: string | null;
  readonly createdAt: string;
  readonly startedAt: string | null;
  readonly finishedAt: string | null;
  readonly stepCount: number;
}

export interface WorkflowRunDetail extends WorkflowRunView {
  readonly steps: readonly WorkflowRunStepView[];
  readonly canRerun: boolean;
}

export interface WorkflowScheduleFireView {
  readonly id: string;
  readonly firedAt: string;
  /**
   * NULL WHEN THE VIEWER MAY NOT READ THE OBJECT (roles-and-permissions.md D14.1).
   *
   * D34's header reads "Monday, 08:00 - 500 of 900 records", and `900` is a HIT COUNT over
   * an object the viewer may not be entitled to read. That is exactly the class of leak
   * `records/search.ts` guards against by applying object permission BEFORE targets are
   * built. So the counts are computed against the VIEWER's readable object set, and a
   * viewer who cannot read the object sees the fire with no counts rather than a count of
   * rows they cannot reach.
   */
  readonly matched: number | null;
  readonly started: number | null;
  readonly capped: boolean;
}

/**
 * The instance switch (D31), in the `effectiveForcePending` shape: the env var is a FLOOR
 * the UI cannot lower, so a deploy-time decision to keep the brake on survives anybody
 * clicking in the app.
 */
export interface WorkflowSettings {
  readonly workflowsPaused: boolean;
  readonly envFloor: boolean;
  readonly updatedAt: string | null;
  readonly updatedByName: string | null;
}

// --- zod ---------------------------------------------------------------------

const stepIdSchema = z.string().regex(WORKFLOW_STEP_ID_PATTERN, 'not a workflow step id');

export const stepTargetSchema: z.ZodType<StepTarget> = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('trigger_record') }),
  z.strictObject({ kind: z.literal('relation'), fieldId: z.uuid() }),
  z.strictObject({ kind: z.literal('step_record'), stepId: stepIdSchema }),
]);

export const taskAssigneeSchema: z.ZodType<TaskAssignee> = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('user'), userId: z.uuid() }),
  z.strictObject({
    kind: z.literal('relation_user'),
    target: stepTargetSchema,
    fieldId: z.uuid(),
  }),
]);

export const valueSourceSchema: z.ZodType<ValueSource> = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('literal'), value: z.unknown() }),
  z.strictObject({
    kind: z.literal('field'),
    target: stepTargetSchema,
    fieldId: z.uuid(),
    /** D21's DATETIME-to-DATE narrowing; the type pair is checked by `workflows/validate.ts`. */
    cast: z.literal('date').optional(),
  }),
  z.strictObject({ kind: z.literal('now') }),
  z.strictObject({
    kind: z.literal('today_plus_days'),
    days: z.number().int().min(-3650).max(3650),
  }),
  z.strictObject({ kind: z.literal('empty') }),
]);

const valueAssignmentSchema = z.strictObject({
  fieldId: z.uuid(),
  source: valueSourceSchema,
});

/** At least one assignment: a "change the record" step that changes nothing is a no-op. */
const assignmentsSchema = z.array(valueAssignmentSchema).min(1).max(50);

export const workflowStepSchema: z.ZodType<WorkflowStep> = z.discriminatedUnion('kind', [
  z.strictObject({
    id: stepIdSchema,
    kind: z.literal('update_record'),
    target: stepTargetSchema,
    values: assignmentsSchema,
  }),
  z.strictObject({
    id: stepIdSchema,
    kind: z.literal('create_record'),
    objectMetadataId: z.uuid(),
    values: assignmentsSchema,
  }),
  z.strictObject({
    id: stepIdSchema,
    kind: z.literal('create_task'),
    target: stepTargetSchema,
    title: z.string().min(1).max(WORKFLOW_TASK_TITLE_MAX),
    assigneeUserId: z.uuid().nullable().optional(),
    assignee: taskAssigneeSchema.nullable().optional(),
    dueDate: valueSourceSchema.nullable().optional(),
  }),
  z.strictObject({
    id: stepIdSchema,
    kind: z.literal('http_request'),
    target: stepTargetSchema,
    url: z.url(),
    headers: z
      .array(z.strictObject({ name: z.string().min(1).max(120), value: z.string().max(2000) }))
      .max(WORKFLOW_MAX_HTTP_HEADERS),
    sign: z.boolean(),
  }),
  z.strictObject({
    id: stepIdSchema,
    kind: z.literal('propose_change'),
    target: stepTargetSchema,
    values: assignmentsSchema,
  }),
]);

export const workflowTriggerSchema: z.ZodType<WorkflowTrigger> = z.discriminatedUnion('kind', [
  z.strictObject({
    kind: z.literal('record_event'),
    objectMetadataId: z.uuid(),
    actions: z.array(z.literal([...WORKFLOW_TRIGGER_ACTIONS])).min(1),
    fieldIds: z.array(z.uuid()).max(100).optional(),
  }),
  z.strictObject({
    kind: z.literal('schedule'),
    cron: z.string().refine(isValidCron, 'not a five-field cron expression'),
    objectMetadataId: z.uuid(),
  }),
  z.strictObject({ kind: z.literal('manual'), objectMetadataId: z.uuid() }),
]);

/**
 * THE WHOLE DRAFT, never a patch of one step. A step list is a DOCUMENT: partial updates
 * of a document need a merge policy, and there is no concurrent-editing story worth
 * building for three people (D45).
 *
 * THE STEP CAP IS ENFORCED HERE, on the server, and not only by a disabled button
 * (obligation 26). Duplicate step ids are refused for the same reason: `step_record`
 * targets resolve by id, and two steps sharing one would make "the record the earlier step
 * made" ambiguous.
 */
export const workflowDraftSchema = z
  .strictObject({
    trigger: workflowTriggerSchema,
    condition: viewFilterNodeSchema.nullable(),
    steps: z.array(workflowStepSchema).max(WORKFLOW_MAX_STEPS),
  })
  .refine(
    (draft) => new Set(draft.steps.map((step) => step.id)).size === draft.steps.length,
    'two steps share an id, so an earlier step cannot be referred to unambiguously',
  );

export type WorkflowDraftInput = z.infer<typeof workflowDraftSchema>;

export const createWorkflowSchema = z.strictObject({
  name: z.string().min(1).max(WORKFLOW_NAME_MAX),
  description: z.string().max(2000).nullable().optional(),
  /** Required: a workflow with no object has no subject record, and D2 forbids that. */
  trigger: workflowTriggerSchema,
});

export type CreateWorkflowInput = z.infer<typeof createWorkflowSchema>;

export const patchWorkflowSchema = z.strictObject({
  name: z.string().min(1).max(WORKFLOW_NAME_MAX).optional(),
  description: z.string().max(2000).nullable().optional(),
  isEnabled: z.boolean().optional(),
});

export type PatchWorkflowInput = z.infer<typeof patchWorkflowSchema>;

export const runWorkflowSchema = z.strictObject({ recordId: z.uuid() });

export const listWorkflowRunsSchema = z.strictObject({
  segment: z.literal([...WORKFLOW_RUN_SEGMENTS]).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  /** Keyset on the uuidv7 primary key: id order IS start order. */
  cursor: z.uuid().optional(),
});

export const listWorkflowFiresSchema = z.strictObject({
  limit: z.coerce.number().int().min(1).max(50).default(10),
  cursor: z.uuid().optional(),
});

export const workflowSettingsSchema = z.strictObject({ workflowsPaused: z.boolean() });

/** Actor is echoed on a manual run so the run list can say who fired it. */
export const workflowStartedBySchema = actorSchema;

// --- the sentence (D37, obligation 25) ---------------------------------------

/**
 * WHAT A SENTENCE NEEDS TO KNOW, and nothing more.
 *
 * A lookup rather than a metadata snapshot, so the same renderer serves the browser (which
 * holds `FieldMetadata`) and the server (which holds `FieldSpec`) without either shape
 * leaking into `shared`. An id nothing resolves renders as a phrase that says so, never as
 * a uuid - the ABSENT-vs-EMPTY rule `usePanels.ts` set, applied to copy.
 */
export interface WorkflowVocabulary {
  objectLabel(objectMetadataId: string): string | undefined;
  fieldLabel(fieldId: string): string | undefined;
  /** SELECT option labels, so "Health is red" does not read as "Health is opt_a1b2c3". */
  optionLabel?(fieldId: string, optionId: string): string | undefined;
  userName?(userId: string): string | undefined;
}

export const MISSING_OBJECT_PHRASE = 'a deleted object';
/**
 * A FIELD THAT IS GONE READS AS A CLAUSE, NOT AS A NAME (audit P3-11).
 *
 * It was "a deleted field", which is grammatically fine and lands in a NAME slot -
 * "Change the Agency of this record: set a deleted field" reads as a step that sets the
 * Agency TO something deleted, which is not what it does and not what happened. The
 * phrase now says what is true of the step rather than naming a thing, so the reader can
 * tell a broken step from a working one at a glance. The one clause is enough for every
 * slot it lands in: "watching a field that no longer exists", "a field that no longer
 * exists is not empty", "Ask for approval to change a field that no longer exists".
 */
export const MISSING_FIELD_PHRASE = 'a field that no longer exists';

function objectName(vocab: WorkflowVocabulary, id: string): string {
  return vocab.objectLabel(id) ?? MISSING_OBJECT_PHRASE;
}

function fieldName(vocab: WorkflowVocabulary, id: string): string {
  return vocab.fieldLabel(id) ?? MISSING_FIELD_PHRASE;
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * The common shapes in words, everything else verbatim.
 *
 * A cron expression a person typed is not one this function should guess at: an
 * unrecognised shape is printed as itself, which is honest and still checkable, rather
 * than described wrongly. Only `m h * * d` and `m h * * *` are claimed.
 */
export function describeCron(expression: string): string {
  const parts = expression.trim().split(/\s+/);
  const [minute, hour, dom, month, dow] = parts;
  if (
    parts.length !== 5 ||
    minute === undefined ||
    hour === undefined ||
    !/^\d{1,2}$/.test(minute) ||
    !/^\d{1,2}$/.test(hour) ||
    dom !== '*' ||
    month !== '*'
  ) {
    return `on the schedule ${expression}`;
  }
  const at = `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
  if (dow === '*') return `every day at ${at}`;
  if (dow !== undefined && /^\d$/.test(dow)) {
    const day = WEEKDAYS[Number(dow)];
    if (day !== undefined) return `every ${day} at ${at}`;
  }
  return `on the schedule ${expression}`;
}

const TRIGGER_ACTION_PHRASES: Readonly<Record<WorkflowTriggerAction, string>> = {
  created: 'is created',
  updated: 'changes',
  soft_deleted: 'is archived',
};

export function describeTrigger(trigger: WorkflowTrigger, vocab: WorkflowVocabulary): string {
  switch (trigger.kind) {
    case 'record_event': {
      const phrases = WORKFLOW_TRIGGER_ACTIONS.filter((action) =>
        trigger.actions.includes(action),
      ).map((action) => TRIGGER_ACTION_PHRASES[action]);
      const gate =
        trigger.fieldIds === undefined || trigger.fieldIds.length === 0
          ? ''
          : `, watching ${joinWith(
              trigger.fieldIds.map((id) => fieldName(vocab, id)),
              'and',
            )}`;
      return `When a ${objectName(vocab, trigger.objectMetadataId)} ${joinWith(
        phrases,
        'or',
      )}${gate}`;
    }
    case 'schedule':
      return `${capitalise(describeCron(trigger.cron))}, for every ${objectName(
        vocab,
        trigger.objectMetadataId,
      )}`;
    case 'manual':
      return `When you run it on a ${objectName(vocab, trigger.objectMetadataId)}`;
    default:
      return assertNever(trigger, 'describeTrigger');
  }
}

/**
 * THE CONDITION AS A SENTENCE, over the same eighteen labels the filter editor uses
 * (`shared/src/filter-labels.ts`). "Always" when there is none, which is the state the
 * ONLY IF card renders and the state most workflows are in.
 */
export function describeCondition(
  condition: ViewFilterNode | null,
  vocab: WorkflowVocabulary,
): string {
  if (condition === null) return 'Always';
  const parts = conditionParts(condition, vocab);
  if (parts.length === 0) return 'Always';
  const word = condition.kind === 'group' && condition.combinator === 'or' ? ' or ' : ' and ';
  return parts.join(word);
}

function conditionParts(node: ViewFilterNode, vocab: WorkflowVocabulary): readonly string[] {
  if (node.kind === 'group') {
    return node.children.flatMap((child) => conditionParts(child, vocab));
  }
  /**
   * NO ARM, BY DECISION (`docs/decisions/relation-filters.md` D25, D26 row 14). A
   * workflow condition carrying a related node is refused at SAVE time by
   * `workflows/validate.ts`, so no stored condition can reach this walker with one. This
   * guard is the type-level consequence of the AST union growing a third member, not a
   * second opinion about what a related condition means - the refusal is what is pinned
   * (obligation 31), not this line.
   */
  if (node.kind === 'related') return [];
  const label = fieldName(vocab, node.fieldId);
  const operator = FILTER_OPERATOR_LABELS[node.operator];
  if (isNullaryOperator(node.operator)) return [`${label} ${operator}`];
  if (isRelativeDateOperand(node.value)) {
    return [`${label} is ${RELATIVE_DATE_LABELS[node.value.relative].toLowerCase()}`];
  }
  if (Array.isArray(node.value)) {
    const labels = node.value.map((entry) => operandLabel(vocab, node.fieldId, entry));
    return [`${label} ${operator} ${labels.join(', ')}`];
  }
  return [`${label} ${operator} ${operandLabel(vocab, node.fieldId, node.value)}`];
}

function operandLabel(vocab: WorkflowVocabulary, fieldId: string, value: unknown): string {
  if (typeof value !== 'string') return String(value);
  return vocab.optionLabel?.(fieldId, value) ?? value;
}

/**
 * A STEP THAT SETS NOTHING THAT STILL EXISTS SAYS SO (audit P3-11).
 *
 * "Change the Agency of this record: set a field that no longer exists" is honest and is
 * still the wrong shape: the colon promises a list of fields and the list has none. When
 * EVERY field a step writes is gone the sentence drops the promise and states the fact,
 * which is the same absent-vs-empty rule the phrase itself follows. A PARTIAL loss keeps
 * the list, because the surviving fields are what the step still does.
 */
function fieldList(
  ids: readonly string[],
  vocab: WorkflowVocabulary,
): { readonly text: string; readonly allGone: boolean } {
  const allGone = ids.length > 0 && ids.every((id) => vocab.fieldLabel(id) === undefined);
  return {
    text: joinWith(
      ids.map((id) => fieldName(vocab, id)),
      'and',
    ),
    allGone,
  };
}

/** What one step DOES, in one line, for the step row and for the run detail. */
export function describeStep(step: WorkflowStep, vocab: WorkflowVocabulary): string {
  switch (step.kind) {
    case 'update_record': {
      const target = describeTarget(step.target, vocab);
      const fields = fieldList(
        step.values.map((value) => value.fieldId),
        vocab,
      );
      return fields.allGone
        ? `Change ${target}: the ${step.values.length === 1 ? 'field' : 'fields'} this step wrote no longer ${step.values.length === 1 ? 'exists' : 'exist'}`
        : `Change ${target}: set ${fields.text}`;
    }
    case 'create_record':
      return `Create a ${objectName(vocab, step.objectMetadataId)}`;
    case 'create_task':
      return `Create the task "${step.title}"${describeAssignee(step, vocab)} on ${describeTarget(
        step.target,
        vocab,
      )}`;
    case 'http_request':
      return `Send ${describeTarget(step.target, vocab)} to ${hostOf(step.url)}`;
    case 'propose_change':
      return `Ask for approval to change ${joinWith(
        step.values.map((value) => fieldName(vocab, value.fieldId)),
        'and',
      )} on ${describeTarget(step.target, vocab)}`;
    default:
      return assertNever(step, 'describeStep');
  }
}

/**
 * WHO THE TASK GOES TO, as the clause that sits inside the step's sentence
 * (`phase-b-coach.md` D9).
 *
 * A FIXED PERSON READS AS A NAME and a person taken from the record reads as a POSSESSIVE:
 * "assigned to the agency's CSM" is the sentence the playbook is for, and it says out loud
 * that the answer is different for every record the workflow runs on. The hop's own two
 * labels carry it - the relation ("Agency") and the field on the object it points at
 * ("CSM") - so nothing here has to know what a CSM is.
 *
 * A hop that starts on the trigger record itself has no possessive to make, so it says
 * "this record's CSM": the same fact without a relation name that does not exist.
 */
function describeAssignee(step: CreateTaskStep, vocab: WorkflowVocabulary): string {
  const assignee = step.assignee;
  if (assignee === undefined || assignee === null) {
    if (step.assigneeUserId === undefined || step.assigneeUserId === null) return '';
    return ` for ${vocab.userName?.(step.assigneeUserId) ?? 'somebody who has left'}`;
  }
  if (assignee.kind === 'user') {
    return ` for ${vocab.userName?.(assignee.userId) ?? 'somebody who has left'}`;
  }
  const who = fieldName(vocab, assignee.fieldId);
  if (assignee.target.kind !== 'relation') return ` assigned to this record's ${who}`;
  return ` assigned to the ${lowerFirst(fieldName(vocab, assignee.target.fieldId))}'s ${who}`;
}

export function describeTarget(target: StepTarget, vocab: WorkflowVocabulary): string {
  switch (target.kind) {
    case 'trigger_record':
      return 'this record';
    case 'relation':
      return `the ${fieldName(vocab, target.fieldId)} of this record`;
    case 'step_record':
      return 'the record an earlier step created';
    default:
      return assertNever(target, 'describeTarget');
  }
}

/** The host, never the whole URL: a step row is a line, not a query string. */
function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

/**
 * THE WHOLE WORKFLOW IN ONE LINE, so a person can check their intent without reading the
 * cards. A sentence that lies about what a workflow does is worse than no sentence
 * (obligation 25), which is why the step half degrades to a COUNT past two rather than
 * summarising: three clauses is a sentence, six is a paragraph nobody reads.
 */
export function describeWorkflow(
  document: WorkflowVersionDocument,
  vocab: WorkflowVocabulary,
): string {
  const when = describeTrigger(document.trigger, vocab);
  const onlyIf =
    document.condition === null ? '' : `, only if ${describeCondition(document.condition, vocab)}`;
  if (document.steps.length === 0) return `${when}${onlyIf}, then nothing yet`;
  const then =
    document.steps.length <= 2
      ? joinWith(
          document.steps.map((step) => lowerFirst(describeStep(step, vocab))),
          'and',
        )
      : `do ${document.steps.length} things`;
  return `${when}${onlyIf}, then ${then}`;
}

function joinWith(parts: readonly string[], word: string): string {
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0] as string;
  return `${parts.slice(0, -1).join(', ')} ${word} ${parts[parts.length - 1] as string}`;
}

function capitalise(text: string): string {
  return text.length === 0 ? text : `${text.slice(0, 1).toUpperCase()}${text.slice(1)}`;
}

function lowerFirst(text: string): string {
  return text.length === 0 ? text : `${text.slice(0, 1).toLowerCase()}${text.slice(1)}`;
}

// --- run-state copy (D41, red-team N10) --------------------------------------

/**
 * EVERY STATUS HAS WORDS, because a status enum that reaches a screen is how a person ends
 * up reading `blocked_recursion`. The two guard states are phrased as the guard WORKING,
 * which is what they are.
 */
export const WORKFLOW_RUN_STATUS_SENTENCES: Readonly<Record<WorkflowRunStatus, string>> = {
  queued: 'Waiting to start.',
  running: 'Running now.',
  succeeded: 'Ran through to the end.',
  failed: 'A step failed and the run stopped.',
  interrupted: 'The worker stopped mid-step. Check before rerunning.',
  cancelled: 'Stopped: workflows were paused while this was running.',
  blocked_recursion: 'Stopped: this ran as part of a chain of automations that got too long.',
  waiting: 'Waiting.',
};

export const WORKFLOW_RUN_STATUS_LABELS: Readonly<Record<WorkflowRunStatus, string>> = {
  queued: 'Queued',
  running: 'Running',
  succeeded: 'Succeeded',
  failed: 'Failed',
  interrupted: 'Interrupted',
  cancelled: 'Stopped',
  blocked_recursion: 'Stopped',
  waiting: 'Waiting',
};

export const WORKFLOW_STEP_KIND_LABELS: Readonly<Record<WorkflowStepKind, string>> = {
  update_record: 'Change the record',
  create_record: 'Create a record',
  create_task: 'Create a task',
  http_request: 'Send to a URL',
  propose_change: 'Ask for approval',
};
